import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { extractApiError } from '@/lib/api';
import { suggestedOpportunitiesService } from '../services/suggested-opportunities.service';
import type { ListSuggestedFilters } from '../types/suggested-opportunities.types';

/**
 * Sprint 5b.1.5 v2 — Hooks TanStack Query pour « Opportunités suggérées ».
 */

const KEYS = {
  list: (filters?: ListSuggestedFilters) =>
    ['suggested-opportunities', 'list', filters] as const,
  stats: (filters?: ListSuggestedFilters) =>
    ['suggested-opportunities', 'stats', filters] as const,
};

export function useSuggestedOpportunities(filters: ListSuggestedFilters = {}) {
  return useQuery({
    queryKey: KEYS.list(filters),
    queryFn: () => suggestedOpportunitiesService.list(filters),
    staleTime: 60 * 1000, // 1 min — les scores ne changent qu'au cron ou au rescore
  });
}

export function useSuggestedStats(filters: ListSuggestedFilters = {}) {
  return useQuery({
    queryKey: KEYS.stats(filters),
    queryFn: () => suggestedOpportunitiesService.stats(filters),
    staleTime: 60 * 1000,
  });
}

export function useRecomputeSuggestions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => suggestedOpportunitiesService.recompute(),
    onSuccess: () => {
      toast.success(
        'Recherche d\'AO lancée — les suggestions seront disponibles dans ~5 secondes',
      );
      // Invalidation différée pour laisser le temps au job de fetch + scoring de tourner
      setTimeout(() => {
        qc.invalidateQueries({ queryKey: ['suggested-opportunities'] });
      }, 6000);
    },
    onError: (err: unknown) => {
      toast.error(extractApiError(err));
    },
  });
}
